/**
 * Contact search helpers — used by Clients, Leads, and Pipeline searches.
 *
 * Goals:
 *   - Phone-number matches regardless of formatting:
 *     "2039096881", "203-909-6881", "(203) 909-6881" should all be equal.
 *   - Safe against mixed-type fields (address can be string OR object).
 *   - Cheap; all operations run in-memory per-keystroke without memoization
 *     because list sizes are modest (<500 leads/clients in practice).
 */

/** Keep only digits. "(203) 909-6881" → "2039096881". */
export function digits(s) {
  if (s == null) return '';
  return String(s).replace(/\D/g, '');
}

/** True if the query is a phone-ish search (≥3 consecutive digits when
 *  formatting is stripped). Keeps normal text searches unaffected. */
export function isPhoneQuery(q) {
  return digits(q).length >= 3;
}

/**
 * Flatten a possibly-nested address into a single searchable lowercase string.
 * Accepts:
 *   - string → lowercased as-is
 *   - object → joins street/city/state/zip fields present
 *   - null/undefined → ''
 * NEVER throws — this is the root cause of the Clients search crash.
 */
export function addressString(addr) {
  if (!addr) return '';
  if (typeof addr === 'string') return addr.toLowerCase();
  if (typeof addr === 'object') {
    const parts = [];
    const keys = ['street', 'streetNumber', 'streetName', 'city', 'state', 'zip', 'postalCode', 'country'];
    keys.forEach((k) => {
      const v = addr[k];
      if (v != null && v !== '') parts.push(String(v));
    });
    return parts.join(' ').toLowerCase();
  }
  return '';
}

/** Lowercase any primitive field safely. Returns '' for non-strings. */
export function safeLower(v) {
  if (v == null) return '';
  return String(v).toLowerCase();
}

/**
 * Returns true if the record matches the query across name / email / phone /
 * address / (extras). Handles phone-normalization automatically: if the query
 * looks phone-ish, we compare against the digits-only phone field.
 *
 * @param {Object} record - a lead, client, or anything with these fields
 * @param {string} query - raw user input from the search box
 * @param {Object} opts
 * @param {string} opts.nameField - which field to use as the display name
 *                                  (default 'name'; leads use custom logic)
 * @param {string[]} opts.extraTextFields - additional fields to include in text match
 * @param {string} opts.resolvedName - already-computed display name (overrides nameField)
 * @param {string[]} opts.phoneFields - phone fields to normalize (default ['phone'])
 */
export function matchesQuery(record, query, opts = {}) {
  const q = (query || '').trim();
  if (!q) return true;
  if (!record) return false;
  const { nameField = 'name', extraTextFields = [], resolvedName, phoneFields = ['phone'] } = opts;

  const lower = q.toLowerCase();
  const qDigits = digits(q);
  const isPhone = qDigits.length >= 3;

  // 1) Phone: normalized digits-only match on any phone field.
  //    Runs first because it's the most decisive signal for "caller on the line".
  if (isPhone) {
    for (const pf of phoneFields) {
      const d = digits(record[pf]);
      if (d && d.includes(qDigits)) return true;
    }
    // Fall through — caller may still be typing digits that are part of an
    // address (street number) or name, so keep text match active.
  }

  // 2) Text: name, email, address, and any extras.
  const name = resolvedName != null ? safeLower(resolvedName) : safeLower(record[nameField]);
  if (name.includes(lower)) return true;
  if (safeLower(record.email).includes(lower)) return true;
  if (addressString(record.address).includes(lower)) return true;

  for (const field of extraTextFields) {
    if (safeLower(record[field]).includes(lower)) return true;
  }

  return false;
}
